import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { getCurrentUser } from '../../features/auth/authSlice';
import Loader from '../common/Loader';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, roles }) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { isAuthenticated, user, loading } = useSelector((state: RootState) => state.auth);
  const token = localStorage.getItem('token');

  useEffect(() => {
    // If we have a token but not authenticated, try to get current user
    if (token && !isAuthenticated && !loading) {
      dispatch(getCurrentUser());
    }
  }, [token, isAuthenticated, loading, dispatch]);

  // Show loader while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader size="large" text="Verifying authentication..." />
      </div>
    );
  }

  // If no token or not authenticated, redirect to login
  if (!token || !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access if roles are specified
  if (roles && user && !roles.includes(user.role)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-red-800 mb-4">Access Denied</h2>
          <p className="text-red-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
